import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OffersBlock from "@/components/seo/OffersBlock";
import SeoSiloLinks from "@/components/seo/SeoSiloLinks";
import PopularPagesBlock from "@/components/seo/PopularPagesBlock";
import RecentPagesBlock from "@/components/seo/RecentPagesBlock";
import EditorialEnrichment from "@/components/seo/EditorialEnrichment";
import Breadcrumbs from "@/components/Breadcrumbs";
import LastUpdated from "@/components/LastUpdated";
import { Link } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { Tv, Smartphone, Zap, Shield, Trophy, Radio, ArrowRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import TestRequestFormWithCountries from "@/components/TestRequestFormWithCountries";
import FloatingTestCTA from "@/components/FloatingTestCTA";
import { useSeoPageView } from "@/hooks/useSeoPageView";
import { buildArticleSchema, buildBreadcrumbJsonLd, organizationSchema } from "@/lib/seo-schemas";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FootballMatchPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: match, isLoading } = useQuery({
    queryKey: ["football-match", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("football_matches")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  // Fetch 3 other matches for "Voir aussi"
  const { data: relatedMatches } = useQuery({
    queryKey: ["football-related", slug],
    queryFn: async () => {
      const { data } = await supabase
        .from("football_matches")
        .select("slug, home_team, away_team, competition, match_date")
        .eq("published", true)
        .neq("slug", slug!)
        .order("match_date", { ascending: true })
        .limit(3);
      return data || [];
    },
    enabled: !!slug,
  });

  useSeoPageView("football", slug, match ? `${match.home_team} vs ${match.away_team}` : undefined);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!match) return <NotFound />;

  const matchday = (match as any).matchday as number | null;
  const status = ((match as any).status as string) || "SCHEDULED";
  const matchTitle = `${match.home_team} vs ${match.away_team}`;

  const statusLabel = status === "FINISHED" ? "Match terminé" : status === "LIVE" || status === "IN_PLAY" ? "🔴 En direct maintenant" : "À venir";
  const statusBadgeClass = status === "FINISHED"
    ? "bg-muted text-muted-foreground border-border"
    : status === "LIVE" || status === "IN_PLAY"
      ? "bg-destructive/20 text-destructive border-destructive/30"
      : "bg-secondary/20 text-secondary border border-secondary/30";

  const title = `${matchTitle} streaming HD – Regarder le match en direct`;
  const descriptionParts = [`Regardez ${matchTitle} en streaming live HD.`, `Match ${match.competition}`];
  if (matchday) descriptionParts.push(`Journée ${matchday}`);
  descriptionParts.push(`le ${new Date(match.match_date).toLocaleDateString("fr-FR")}`);
  if (match.stadium) descriptionParts.push(`au ${match.stadium}`);
  descriptionParts.push("Accès illimité avec RoiDuStreaming.");
  const description = descriptionParts.join(" ");

  const canonicalUrl = `https://roidustreaming.fr/football/${match.slug}`;
  const matchImage = match.image_url || "https://roidustreaming.fr/images/default.jpg";
  const imageAlt = `Match ${matchTitle} en streaming`;

  const matchDateFormatted = new Date(match.match_date).toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric"
  });

  const startDateTime = `${match.match_date}T${match.match_time || "20:00"}:00`;
  // endDate = startDate + 2h (football match duration)
  const endDate = (() => {
    const d = new Date(startDateTime);
    d.setHours(d.getHours() + 2);
    return d.toISOString();
  })();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: matchTitle,
    description,
    url: canonicalUrl,
    startDate: startDateTime,
    endDate,
    eventStatus: status === "FINISHED" ? "https://schema.org/EventCompleted" : status === "LIVE" || status === "IN_PLAY" ? "https://schema.org/EventRescheduled" : "https://schema.org/EventScheduled",
    sport: "Football",
    homeTeam: { "@type": "SportsTeam", name: match.home_team },
    awayTeam: { "@type": "SportsTeam", name: match.away_team },
    competitor: [
      { "@type": "SportsTeam", name: match.home_team },
      { "@type": "SportsTeam", name: match.away_team },
    ],
    performer: [
      { "@type": "SportsTeam", name: match.home_team },
      { "@type": "SportsTeam", name: match.away_team },
    ],
    ...(match.image_url && { image: match.image_url }),
    location: {
      "@type": "Place",
      name: match.stadium || `Stade ${match.home_team}`,
      address: {
        "@type": "PostalAddress",
        addressCountry: "FR",
      },
    },
    organizer: { "@type": "Organization", name: "RoiDuStreaming", url: "https://roidustreaming.fr" },
    offers: {
      "@type": "Offer",
      name: "Abonnement IPTV RoiDuStreaming",
      price: "5.00",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://roidustreaming.fr/nos-offres",
      validFrom: "2025-01-01",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `Quand a lieu ${matchTitle} ?`,
        acceptedAnswer: { "@type": "Answer", text: `${matchTitle} se joue le ${matchDateFormatted}${match.match_time ? ` à ${match.match_time}` : ""}.` }
      },
      {
        "@type": "Question",
        name: `À quelle heure débute ${matchTitle} ?`,
        acceptedAnswer: { "@type": "Answer", text: match.match_time ? `Le coup d'envoi de ${matchTitle} est prévu à ${match.match_time}.` : `L'heure exacte de ${matchTitle} sera confirmée prochainement. Restez connecté sur RoiDuStreaming.` }
      },
      {
        "@type": "Question",
        name: `Sur quelle chaîne est diffusé ${matchTitle} ?`,
        acceptedAnswer: { "@type": "Answer", text: `${matchTitle} est diffusé sur les chaînes sportives beIN Sports, Canal+, RMC Sport ou DAZN selon les droits. Avec RoiDuStreaming, accédez à toutes ces chaînes en HD/4K à partir de 5€/mois.` }
      },
      {
        "@type": "Question",
        name: `Comment regarder ${matchTitle} en direct ?`,
        acceptedAnswer: { "@type": "Answer", text: `Abonnez-vous à RoiDuStreaming à partir de 5€/mois ou testez gratuitement pendant 24h. Accédez à ${matchTitle} et à toutes les chaînes sport en HD et 4K sur Smart TV, Android, iPhone, PC et Fire Stick.` }
      },
      {
        "@type": "Question",
        name: `Où se joue ${matchTitle} ?`,
        acceptedAnswer: { "@type": "Answer", text: match.stadium ? `${matchTitle} se joue au ${match.stadium}.` : `Le stade de ${matchTitle} sera confirmé prochainement.` }
      }
    ]
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "Football", href: "/football" },
    { label: matchTitle, href: `/football/${match.slug}` },
  ];

  const advantages = [
    { icon: Tv, title: "Full HD & 4K", desc: "Image cristalline en direct" },
    { icon: Zap, title: "Zéro décalage", desc: "Streaming live sans lag" },
    { icon: Smartphone, title: "Multi-appareils", desc: "Smart TV, mobile, tablette, PC" },
    { icon: Shield, title: "Accès sécurisé", desc: "Paiement Stripe 100% sécurisé" },
    { icon: Trophy, title: "Toutes les compétitions", desc: "Ligue 1, UCL, Premier League..." },
    { icon: Radio, title: "beIN, Canal+, DAZN", desc: "Toutes les chaînes sport" },
  ];

  const breadcrumbSchema = buildBreadcrumbJsonLd([
    { name: "Accueil", href: "/" },
    { name: "Football", href: "/football" },
    { name: matchTitle, href: `/football/${match.slug}` },
  ]);

  const seoText = status === "FINISHED"
    ? `Le match ${matchTitle} en ${match.competition}${matchday ? ` (Journée ${matchday})` : ""} est terminé. Revivez les meilleurs moments et tous les matchs en replay sur RoiDuStreaming.`
    : status === "LIVE" || status === "IN_PLAY"
      ? `${matchTitle} est en cours en ce moment ! Suivez ce match de ${match.competition}${matchday ? ` (Journée ${matchday})` : ""} en direct et en HD sur RoiDuStreaming.`
      : `${matchTitle}, match de ${match.competition}${matchday ? ` Journée ${matchday}` : ""}, se jouera le ${matchDateFormatted}${match.match_time ? ` à ${match.match_time}` : ""}${match.stadium ? ` au ${match.stadium}` : ""}. Regardez-le en direct HD sur RoiDuStreaming.`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="RoiDuStreaming" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:image" content={matchImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={matchImage} />
        <meta property="article:published_time" content={`${match.match_date}T00:00:00Z`} />
        <meta property="article:section" content="Football" />
        <meta property="article:tag" content={match.competition} />
        <meta property="article:tag" content={match.home_team} />
        <meta property="article:tag" content={match.away_team} />
        <link rel="preconnect" href="https://tpujpzojoqwytuhgbwtw.supabase.co" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(buildArticleSchema({
          headline: `${matchTitle} ${match.competition} Streaming en Direct HD`,
          description,
          url: canonicalUrl,
          imageUrl: match.image_url,
          datePublished: match.created_at || match.match_date,
          dateModified: match.created_at || new Date().toISOString(),
          section: "Football",
        }))}</script>
      </Helmet>

      <article className="min-h-screen bg-background">
        <Header />
        <Breadcrumbs items={breadcrumbItems} />
        <div className="max-w-6xl mx-auto px-4 pt-3">
          <LastUpdated date={match.created_at || new Date().toISOString()} />
        </div>

        {/* ── NOSCRIPT FALLBACK : données critiques visibles sans JS ── */}
        <noscript>
          <div>
            <h1>{matchTitle} – {match.competition} – Streaming en Direct</h1>
            <p>Match de {match.competition}{matchday ? `, journée ${matchday}` : ""} le {match.match_date}.</p>
            {match.match_time && <p>Coup d'envoi à {match.match_time}.</p>}
            {match.stadium && <p>Stade : {match.stadium}.</p>}
            <p>Regardez {matchTitle} en direct HD sur RoiDuStreaming, à partir de 5€/mois.</p>
            <ul>
              <li><a href="https://roidustreaming.fr">Accueil</a></li>
              <li><a href="https://roidustreaming.fr/football">Football en streaming</a></li>
              <li><a href="https://roidustreaming.fr/nos-offres">Offres IPTV</a></li>
            </ul>
          </div>
        </noscript>

        {/* Hero */}
        <section className="relative min-h-[50vh] flex items-center px-4 overflow-hidden">
          {match.image_url && (
            <img src={match.image_url} alt={imageAlt} className="absolute inset-0 w-full h-full object-cover blur-sm scale-105 opacity-30" loading="eager" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />

          <div className="relative z-10 max-w-6xl mx-auto w-full py-16 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Badge className={statusBadgeClass}>{statusLabel}</Badge>
              <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold bg-secondary/20 text-secondary border border-secondary/30">
                ⚽ {match.competition}{matchday ? ` • J${matchday}` : ""}
              </span>
            </div>
            <div className="flex items-center justify-center gap-4 sm:gap-8 mb-4">
              {match.image_url && (
                <img src={match.image_url} alt={match.home_team} className="h-16 w-16 sm:h-20 sm:w-20 object-contain drop-shadow-lg" loading="eager" />
              )}
              <span className="text-2xl sm:text-3xl font-black text-muted-foreground">VS</span>
              {(match as any).away_image_url && (
                <img src={(match as any).away_image_url} alt={match.away_team} className="h-16 w-16 sm:h-20 sm:w-20 object-contain drop-shadow-lg" loading="eager" />
              )}
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black mb-4 text-foreground leading-tight">
              {match.home_team} vs {match.away_team}
              <span className="block text-primary text-xl md:text-2xl mt-2 font-bold">en Streaming Direct HD & 4K</span>
            </h1>
            <p className="text-muted-foreground text-lg mb-2">
              📅 {matchDateFormatted}
              {match.match_time && ` à ${match.match_time}`}
            </p>
            {match.stadium && <p className="text-muted-foreground mb-6">📍 {match.stadium}</p>}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <TestRequestFormWithCountries
                trigger={
                  <Button className="btn-primary text-primary-foreground font-bold py-5 sm:py-6 px-6 sm:px-8 text-sm sm:text-base rounded-full group w-full sm:w-auto">
                    ⚽ Essayer gratuitement 24h
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                }
              />
              <Link to="/nos-offres" className="w-full sm:w-auto">
                <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 font-bold py-5 sm:py-6 px-6 sm:px-8 text-sm sm:text-base rounded-full w-full sm:w-auto">
                  Voir les offres dès 5€/mois
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="border-y border-border/50 bg-card/50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <span>⚡ Installation en 5 min</span>
            <span>🛟 Support 7j/7</span>
            <span>📺 +15 000 chaînes TV</span>
            <span>🔒 Paiement sécurisé</span>
          </div>
        </section>

        {/* ── TABLE 1 : INFORMATIONS DU MATCH ── */}
        <section className="py-10 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">Informations du match</h2>
            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium w-1/2 bg-card/50">Équipe domicile</th>
                    <td className="px-4 py-3 font-semibold text-foreground">{match.home_team}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Équipe extérieure</th>
                    <td className="px-4 py-3 font-semibold text-foreground">{match.away_team}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Compétition</th>
                    <td className="px-4 py-3 text-foreground">{match.competition}{matchday ? ` — Journée ${matchday}` : ""}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Date</th>
                    <td className="px-4 py-3 text-foreground">{matchDateFormatted}</td>
                  </tr>
                  {match.match_time && (
                    <tr className="border-b border-border/40">
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Heure de coup d'envoi</th>
                      <td className="px-4 py-3 text-foreground font-semibold text-primary">{match.match_time}</td>
                    </tr>
                  )}
                  {match.stadium && (
                    <tr className="border-b border-border/40">
                      <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Stade</th>
                      <td className="px-4 py-3 text-foreground">{match.stadium}</td>
                    </tr>
                  )}
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Statut</th>
                    <td className="px-4 py-3 text-foreground">{statusLabel}</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Type de match</th>
                    <td className="px-4 py-3 text-foreground">{matchday ? "Phase de championnat" : "Match de coupe / compétition"}</td>
                  </tr>
                  <tr>
                    <th className="text-left px-4 py-3 text-muted-foreground font-medium bg-card/50">Contexte</th>
                    <td className="px-4 py-3 text-foreground">{match.home_team} reçoit {match.away_team} dans le cadre de la {match.competition}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── TABLE 2 : STREAMING & DIFFUSION ── */}
        <section className="py-6 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-bold text-foreground mb-4">Comment regarder {match.home_team} vs {match.away_team} en streaming</h2>
            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-primary/5">
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Critère</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">RoiDuStreaming</th>
                    <th className="text-left px-4 py-3 font-semibold text-foreground">Chaînes classiques</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Qualité vidéo</td>
                    <td className="px-4 py-3 text-foreground font-semibold">Full HD & 4K</td>
                    <td className="px-4 py-3 text-muted-foreground">HD selon abonnement</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Prix mensuel</td>
                    <td className="px-4 py-3 text-foreground font-semibold text-secondary">À partir de 5€/mois</td>
                    <td className="px-4 py-3 text-muted-foreground">15€ à 50€/mois</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Appareils compatibles</td>
                    <td className="px-4 py-3 text-foreground">Smart TV, Android, iPhone, PC, Fire Stick</td>
                    <td className="px-4 py-3 text-muted-foreground">Limité par plateforme</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Chaînes sport incluses</td>
                    <td className="px-4 py-3 text-foreground font-semibold">beIN Sports, Canal+, DAZN, RMC Sport</td>
                    <td className="px-4 py-3 text-muted-foreground">1 chaîne par abonnement</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Décalage (lag)</td>
                    <td className="px-4 py-3 text-foreground font-semibold">Zéro décalage</td>
                    <td className="px-4 py-3 text-muted-foreground">Variable</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-muted-foreground font-medium bg-card/30">Essai gratuit</td>
                    <td className="px-4 py-3 text-foreground font-semibold text-secondary">✅ 24h gratuit</td>
                    <td className="px-4 py-3 text-muted-foreground">❌ Non</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3 text-foreground">
              Regardez <span className="text-primary">{matchTitle}</span> en direct
            </h2>
            <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">{seoText}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {advantages.map((adv, i) => (
                <div key={i} className="p-5 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all text-center group">
                  <adv.icon className="w-8 h-8 mx-auto mb-3 text-primary group-hover:scale-110 transition-transform" />
                  <h3 className="font-bold text-foreground mb-1">{adv.title}</h3>
                  <p className="text-sm text-muted-foreground">{adv.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-10 px-4">
          <div className="max-w-3xl mx-auto prose prose-invert prose-sm">
            <h2 className="text-xl font-bold text-foreground">
              {matchTitle} en streaming direct sur RoiDuStreaming
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {seoText} Accédez à toutes les chaînes sportives : beIN Sports, Canal+, DAZN, RMC Sport, depuis une seule plateforme <Link to="/iptv-france" className="text-primary hover:underline">IPTV France</Link>.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Compatible avec tous vos appareils : <Link to="/tutoriels/iptv-smart-tv-android" className="text-primary hover:underline">Smart TV</Link>, <Link to="/tutoriels/iptv-android" className="text-primary hover:underline">Android</Link>, <Link to="/tutoriels/iptv-iphone-ipad" className="text-primary hover:underline">iPhone</Link>, <Link to="/tutoriels/iptv-firestick" className="text-primary hover:underline">Fire Stick</Link>. <Link to="/nos-offres" className="text-primary hover:underline">Abonnement dès 5€/mois</Link>, essai gratuit 24h inclus. Consultez nos <Link to="/avis-clients" className="text-primary hover:underline">avis clients</Link>.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 px-4">
          <div className="max-w-4xl mx-auto rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 border border-primary/30 p-6 sm:p-8 md:p-12 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-foreground">
              Ne ratez pas {matchTitle} !
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto text-sm sm:text-base">
              Plus besoin de multiplier les abonnements. Avec RoiDuStreaming, accédez à toutes les chaînes depuis une seule plateforme pour moins de 60€/an.
            </p>
            <TestRequestFormWithCountries
              trigger={
                <Button className="btn-primary text-primary-foreground font-bold py-5 sm:py-6 px-6 sm:px-10 text-sm sm:text-base rounded-full group w-full sm:w-auto">
                  Démarrer mon essai gratuit 24h
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              }
            />
          </div>
        </section>

        {/* ── FAQ DYNAMIQUE ── */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 text-foreground">
              Questions fréquentes sur {matchTitle}
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {[
                { q: `Quand a lieu ${matchTitle} ?`, a: `${matchTitle} se joue le ${matchDateFormatted}${match.match_time ? ` à ${match.match_time}` : ""}.` },
                { q: `À quelle heure débute ${matchTitle} ?`, a: match.match_time ? `Le coup d'envoi de ${matchTitle} est prévu à ${match.match_time}.` : `L'heure exacte sera confirmée prochainement. Restez connecté sur RoiDuStreaming.` },
                { q: `Sur quelle chaîne est diffusé ${matchTitle} ?`, a: `${matchTitle} est diffusé sur les chaînes sportives beIN Sports, Canal+, RMC Sport ou DAZN. Avec RoiDuStreaming, accédez à toutes ces chaînes en HD/4K à partir de 5€/mois.` },
                { q: `Comment regarder ${matchTitle} en direct ?`, a: `Abonnez-vous à RoiDuStreaming à partir de 5€/mois ou testez gratuitement 24h. Accédez à ${matchTitle} en HD et 4K sur Smart TV, Android, iPhone, PC et Fire Stick.` },
                { q: `Où se joue ${matchTitle} ?`, a: match.stadium ? `${matchTitle} se joue au ${match.stadium}.` : `Le stade de ${matchTitle} sera confirmé prochainement.` },
              ].map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border/50 rounded-xl px-5 bg-card/50">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:text-primary">{faq.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ── VOIR AUSSI ── */}
        {relatedMatches && relatedMatches.length > 0 && (
          <section className="py-10 px-4 bg-card/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-bold text-foreground mb-5">Voir aussi – Matchs à venir</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {relatedMatches.map((m) => (
                  <Link
                    key={m.slug}
                    to={`/football/${m.slug}`}
                    className="flex flex-col gap-1 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all group"
                  >
                    <span className="text-xs text-muted-foreground">{m.competition}</span>
                    <span className="font-bold text-foreground group-hover:text-primary transition-colors text-sm">
                      {m.home_team} vs {m.away_team}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      📅 {new Date(m.match_date).toLocaleDateString("fr-FR")}
                      <ChevronRight className="w-3 h-3 ml-auto" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Editorial Enrichment ── */}
        <EditorialEnrichment content={(match as any).editorial_content} />

        {/* Offers */}
        <div className="max-w-6xl mx-auto px-4">
          <OffersBlock />
        </div>

        {/* Internal Links */}
        <PopularPagesBlock />
        <RecentPagesBlock />
        <SeoSiloLinks category="football" currentSlug={slug} />

        <Footer />
        <FloatingTestCTA />
      </article>
    </>
  );
};

export default FootballMatchPage;
